import { Box, Typography, useTheme } from '@mui/material';
import {
  DataGridPro,
  DataGridProProps,
  GridColDef,
  GridPaginationModel,
  GridRowIdGetter,
  GridSlots,
  GridValidRowModel,
  PropsFromSlot,
} from '@mui/x-data-grid-pro';
import { LicenseInfo } from '@mui/x-license';
import type { BaseQueryFn, TypedUseQueryHookResult } from '@reduxjs/toolkit/dist/query/react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import {
  BaseMutableObject,
  BaseObject,
  ListResponse,
  TransactionDetails,
} from '@verantos-dev/types-common';

import { DataGridOverlay } from './components/DataGridOverlay/DataGridOverlay';
import { DataGridPagination } from './components/DataGridPagination/DataGridPagination';
import { useDataGridMode } from './hooks/useDatagridMode/useDatagridMode';
import { useDataGridSearchParams } from './hooks/useDataGridSearchParams/useDataGridSearchParams';
import { useDataGridState } from './hooks/useDataGridState/useDataGridState';
import WorkflowMessage from '../../custom-components/WorkflowMessage/WorkflowMessage';

LicenseInfo.setLicenseKey(
  '9a8f54655b7c5bcdc3a97fae833c9963Tz05OTg1NixFPTE3NjAyOTY2NzYwMDAsUz1wcm8sTE09c3Vic2NyaXB0aW9uLFBWPVEzLTIwMjQsS1Y9Mg==' // pragma: allowlist secret
);

export interface DataGridMessages {
  /**
   * Overlay message when no rows are displayed
   * @default 'No rows'
   * */
  noRows?: string;

  /**
   * Overlay message when no results are found
   * @default 'No results'
   * */
  noResults?: string;

  /**
   * Error title message
   * @default 'Error'
   */
  errorTitle?: string;

  /**
   * Error message when fetching data
   * @default 'An error occurred while fetching data'
   * */
  error?: string;
}

export type DataGridProps<
  T extends GridValidRowModel,
  ResultType extends TransactionDetails | BaseObject | BaseMutableObject,
  QueryArg,
  BaseQuery extends BaseQueryFn
> = {
  /**
   * @prop {string} id - id of the entry, also used are the key
   */
  id: string;

  /**
   * @prop {'client' | 'server'} mode - mode for search, filter, sort, and pagination
   * @default 'client'
   */
  mode: 'client' | 'server';

  /**
   * @prop {GridRowIdGetter<any>} - getRowId - function to get the row id
   * @returns {void}
   *
   */
  getRowId: GridRowIdGetter<any>;

  /**
   * @prop {} columns - columns for the data grid
   */
  columns: GridColDef<T>[];

  /**
   * @prop {} useFetchDataGridData - RTK Query hook for fetching data
   */
  useFetchDataGridData:
    | ((
        searchParams?: string
      ) => TypedUseQueryHookResult<ListResponse<ResultType>, QueryArg, BaseQuery>)
    | ((searchParams?: string) => void);

  /**
   * @prop {DataGridMessages} messages - messages for the data grid
   */
  messages?: DataGridMessages;

  /**
   * @prop {string} additionalQueryArgs - additional query arguments
   */
  additionalQueryArgs?: string;

  /**
   * @prop {number} initialPageSize - initial page size
   * @default 10
   */
  initialPageSize?: number;

  /**
   * @prop {boolean} disableSearchParamSync - disable search param sync
   * @description The data grid will not sync with the URL search params, this is usefule
   * when you want to control the data grid state from outside the data grid. Or if there will be
   * multiple data grids on the same page.
   * @default false
   */
  disableSearchParamSync?: boolean;

  /**
   * @prop {(data: ListResponse<ResultType>) => number} getRowCount - enable pagination
   * @description Function to get the row count
   * @default (data: ListResponse<ResultType>) => data.data.length
   * @returns {number}
   */
  getRowCount?: (data: ListResponse<ResultType>) => number;
} & Omit<
  DataGridProProps,
  | 'paginationMode'
  | 'sortingMode'
  | 'filterMode'
  | 'getRowId'
  | 'initialPageSize'
  | 'columns'
  | 'loading'
>;

// TODO: figure out styling of row
export const DataGrid = <
  T extends GridValidRowModel,
  ResultType extends TransactionDetails | BaseObject | BaseMutableObject,
  QueryArg,
  BaseQuery extends BaseQueryFn
>({
  id,
  sx,
  mode,
  initialState,
  getRowId,
  slots,
  slotProps,
  pageSizeOptions = [10, 25, 50, 100],
  pagination,
  initialPageSize = 10,
  onPaginationModelChange,
  useFetchDataGridData,
  additionalQueryArgs,
  rows,
  disableSearchParamSync = false,
  messages,
  getRowCount = (data: ListResponse<ResultType>) => data.data.length,
  ...rest
}: DataGridProps<T, ResultType, QueryArg, BaseQuery>) => {
  const theme = useTheme();
  const intl = useIntl();

  const {
    handleFilterModelChange,
    handlePaginationModelChange,
    handleSortModelChange,
    initialState: _initialState,
    filterModel,
    sortModel,
    paginationModel,
  } = useDataGridState(disableSearchParamSync, pageSizeOptions, initialState, {
    pagination,
    initialPageSize,
  }) || {};
  const { filterMode, paginationMode, sortingMode } = useDataGridMode(mode);

  // Fetch data from RTK Query
  const dataGridFetchSearchParams = useDataGridSearchParams(
    mode,
    disableSearchParamSync,
    additionalQueryArgs
  );
  const fetchResults = useFetchDataGridData(dataGridFetchSearchParams);
  const { data, isLoading, isUninitialized, error } = fetchResults || {};

  // Memoize rows to optimize performance
  const memoizedRows = useMemo(() => {
    return rows || data?.data || [];
  }, [rows, data]);

  // get row count: if mode is server, get row count from data, else undefined
  // if data is not available, set rowCount to 0
  const rowCount = mode === 'server' ? (data ? getRowCount(data) : 0) : undefined;

  const memoizedSlots = useMemo(
    () => ({
      footerRowCount: ({
        rowCount,
        visibleRowCount,
      }: {
        rowCount: number;
        visibleRowCount: number;
      }) => (
        <Box mx={theme.spacing(3)} display="flex" alignItems="center">
          <Typography variant="body2" sx={{ fontSize: '12px' }}>
            {visibleRowCount !== rowCount
              ? intl.formatMessage(
                  { id: 'datagrid.footer.rows.totalRowsFiltered' },
                  { visibleRow: visibleRowCount, totalRows: rowCount }
                )
              : intl.formatMessage(
                  { id: 'datagrid.footer.rows.totalRows' },
                  { totalRows: rowCount }
                )}
          </Typography>
        </Box>
      ),
      noRowsOverlay: () => (
        <DataGridOverlay>
          {messages?.noRows || intl.formatMessage({ id: 'dataGrid.overlay.noRows' })}
        </DataGridOverlay>
      ),
      noResultsOverlay: () => (
        <DataGridOverlay>
          {messages?.noResults || intl.formatMessage({ id: 'dataGrid.overlay.noResults' })}
        </DataGridOverlay>
      ),
      pagination: DataGridPagination as PropsFromSlot<GridSlots['pagination']>,
      ...slots,
    }),
    [intl, messages?.noResults, messages?.noRows, slots, theme]
  );

  if (error) {
    return (
      <WorkflowMessage
        id="data-grid-error"
        sx={{
          width: '100%',
          height: '100%',
          minHeight: '400px',
        }}
        title={messages?.errorTitle || intl.formatMessage({ id: 'dataGrid.errorTitle' })}
        message={messages?.error || intl.formatMessage({ id: 'dataGrid.error' })}
      />
    );
  }

  return (
    <DataGridPro
      data-testid={`${id}-MUIDataGrid`}
      disableRowSelectionOnClick={true}
      autosizeOnMount={true}
      {...rest}
      sx={{
        minHeight: '400px',
        flex: 1,
        '& .MuiDataGrid-cell': {
          display: 'flex', // Ensure cells use flexbox
          alignItems: 'center', // Vertically align content
        },
        ...sx, // placing this last to allow for override
      }}
      initialState={_initialState}
      pageSizeOptions={pageSizeOptions}
      pagination={pagination}
      paginationMode={paginationMode}
      paginationModel={paginationModel}
      onPaginationModelChange={(paginationModel: GridPaginationModel) =>
        handlePaginationModelChange(paginationModel)
      }
      sortingMode={sortingMode}
      sortModel={sortModel}
      rows={memoizedRows}
      loading={isLoading || isUninitialized}
      getRowId={getRowId}
      rowCount={rowCount}
      slots={memoizedSlots}
      slotProps={{
        loadingOverlay: {
          variant: 'linear-progress',
          noRowsVariant: 'skeleton',
        },
        ...slotProps,
      }}
    />
  );
};
